import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import StationList from './components/StationList';
import Filters from './components/Filters';
import LocationButton from './components/LocationButton';
import DataFreshness from './components/DataFreshness';
import InstallPrompt from './components/InstallPrompt';
import BottomSheet from './components/BottomSheet';
import FeedbackModal from './components/FeedbackModal';
import useGeolocation from './hooks/useGeolocation';
import useGasData from './hooks/useGasData';
import useTheme from './hooks/useTheme';
import { trackSearch, trackStationClick } from './utils/analytics';
import './styles/tokens.css';
import './styles/App.css';

const Map = lazy(() => import('./components/Map'));
const TrendChart = lazy(() => import('./components/TrendChart'));
const YoYComparison = lazy(() => import('./components/YoYComparison'));
const FuelCalculator = lazy(() => import('./components/FuelCalculator'));

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// URL de compartilhamento: reflete filtros+localização no query string,
// permitindo linkar direto para uma busca (ex: posto perto de casa).
function useShareableUrl({ location, product, radius, brand }) {
  useEffect(() => {
    if (!location) return;
    const params = new URLSearchParams();
    params.set('lat', location.lat.toFixed(5));
    params.set('lng', location.lng.toFixed(5));
    if (product) params.set('product', product);
    if (radius && radius !== 5) params.set('radius', String(radius));
    if (brand) params.set('brand', brand);
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', url);
  }, [location, product, radius, brand]);
}

function readFiltersFromUrl() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    product: params.get('product') || undefined,
    radius: params.get('radius') ? parseInt(params.get('radius'), 10) : undefined,
    brand: params.get('brand') || undefined,
  };
}

function ThemeToggle() {
  const { theme, resolvedTheme, cycle } = useTheme();
  const icon = resolvedTheme === 'dark' ? '☀️' : theme === 'light' ? '🖥️' : '🌙';
  const label =
    theme === null
      ? `Tema: automático (${resolvedTheme === 'dark' ? 'escuro' : 'claro'})`
      : `Tema: ${theme === 'dark' ? 'escuro' : 'claro'}`;
  return (
    <button className="theme-toggle" onClick={cycle} aria-label={label} title={label}>
      {icon}
    </button>
  );
}

function WelcomeState() {
  return (
    <div className="welcome-message">
      <svg className="welcome-icon" viewBox="0 0 64 64" fill="none" aria-hidden="true">
        <rect x="10" y="14" width="28" height="40" rx="3" stroke="currentColor" strokeWidth="2.5" />
        <rect x="16" y="22" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="2.5" />
        <path d="M38 24h7a3 3 0 0 1 3 3v10a4 4 0 0 1-8 0v-9h-2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M17 50h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="24" cy="28" r="2.5" fill="currentColor" />
      </svg>
      <h2>Encontre o posto mais barato</h2>
      <p>
        Permita o acesso à sua localização ou escolha uma cidade para ver os
        preços de combustíveis na sua região.
      </p>
    </div>
  );
}

export default function App() {
  const urlFilters = useRef(readFiltersFromUrl()).current;
  const [product, setProduct] = useState(urlFilters.product || 'GASOLINA');
  const [radius, setRadius] = useState(urlFilters.radius || 5);
  const [brand, setBrand] = useState(urlFilters.brand || '');
  const [selectedStation, setSelectedStation] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { location, error: geoError, loading: geoLoading, requestLocation, setManualLocation } = useGeolocation();
  const { stations, trends, products, brands, loading, error } = useGasData(location, product, radius, brand);
  const isMobile = useIsMobile();

  useShareableUrl({ location, product, radius, brand });

  const prevStationsKey = useRef(null);
  useEffect(() => {
    if (!loading && stations.length > 0) {
      const key = `${product}|${radius}|${brand}|${stations.length}`;
      if (key !== prevStationsKey.current) {
        prevStationsKey.current = key;
        trackSearch({ product, radius, brand, resultsCount: stations.length });
      }
    }
  }, [stations, loading, product, radius, brand]);

  const handleStationClick = (station) => {
    setSelectedStation(station);
    trackStationClick({
      stationName: station.station_name,
      product: station.product,
      price: station.sale_price,
      distanceKm: station.distance_km,
    });
  };

  return (
    <div className="app">
      <InstallPrompt />
      <header className="app-header">
        <ThemeToggle />
        <h1>Preços de Combustíveis</h1>
        <p className="subtitle">Encontre os melhores preços perto de você</p>
      </header>

      <DataFreshness />

      <div className="app-controls">
        <LocationButton
          onRequestLocation={requestLocation}
          onSetManualLocation={setManualLocation}
          loading={geoLoading}
          error={geoError}
        />
        {products.length > 0 && (
          <Filters
            product={product}
            radius={radius}
            brand={brand}
            products={products}
            brands={brands}
            onProductChange={setProduct}
            onRadiusChange={setRadius}
            onBrandChange={setBrand}
          />
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {location ? (
        <div className="app-content">
          <div className="map-section">
            <Suspense fallback={<div className="map-loading">Carregando mapa...</div>}>
              <Map
                location={location}
                stations={stations}
                radiusKm={radius}
                selectedStation={selectedStation}
                onStationClick={handleStationClick}
              />
            </Suspense>
          </div>

          {isMobile ? (
            <BottomSheet>
              <StationList
                stations={stations}
                loading={loading}
                selectedStation={selectedStation}
                onStationClick={handleStationClick}
              />
            </BottomSheet>
          ) : (
            <div className="sidebar">
              <StationList
                stations={stations}
                loading={loading}
                selectedStation={selectedStation}
                onStationClick={handleStationClick}
              />
            </div>
          )}


          <div className="yoy-section">
            <Suspense fallback={<div className="loading-spinner">Carregando comparação...</div>}>
              <YoYComparison trends={trends} product={product} />
            </Suspense>
          </div>

          <div className="chart-section">
            <Suspense fallback={<div className="loading-spinner">Carregando gráfico...</div>}>
              <TrendChart trends={trends} product={product} />
            </Suspense>
          </div>
        </div>
      ) : (
        geoLoading ? (
          <div className="geo-loading-skeleton">
            <div className="skeleton-map" />
            <div className="skeleton-cards">
              <div className="skeleton-card" />
              <div className="skeleton-card" />
              <div className="skeleton-card" />
            </div>
          </div>
        ) : !geoError ? (
          <WelcomeState />
        ) : null
      )}

      <button className="feedback-fab" onClick={() => setFeedbackOpen(true)}>
        Enviar feedback
      </button>

      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        context={{
          city: location ? null : null,
          location,
          product,
          brand,
          radius,
          resultsCount: stations.length,
          selectedStation,
        }}
      />

      <footer className="app-footer">
        <p>Dados: ANP - Agência Nacional do Petróleo, Gás Natural e Biocombustíveis</p>
      </footer>
    </div>
  );
}
